import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserPlus, Search, Plus, Trash2 } from 'lucide-react';
import { PAGES, PAGE_GROUPS } from '@nu/shared';
import Modal from '../components/Modal.jsx';
import { api, ApiError } from '../api/client.js';
import PageAccess from './PageAccess.jsx';

// Who can use Nano Utilities and as what. Each level is one NU- group in the
// "Nano Utilities" admin unit; the API keeps everyone in exactly one.
export const LEVELS = [
  { value: 'field', label: 'Field', hint: 'Field app only', group: 'NU-Field' },
  { value: 'office', label: 'Office', hint: 'Office web only', group: 'NU-Office' },
  { value: 'both', label: 'Field + office', hint: 'Both, can switch view', group: 'NU-Both' },
  { value: 'admin', label: 'Master admin', hint: 'Everything, including this page', group: 'NU-Master-Admins' },
];
const LABEL = Object.fromEntries(LEVELS.map((l) => [l.value, l.label]));

function problem(e) {
  if (e?.code === 'offline') return 'No connection. Permissions can only be changed online.';
  if (e instanceof ApiError) return e.message;
  return 'Something went wrong. Try again.';
}

// Three tabs: Page access (what each person can open, kept in our database),
// Sign-in access (who can sign in and to which shell: the NU- groups) and
// Profiles (named sets of pages to give someone in one go).
export default function Permissions({ me }) {
  const [tab, setTab] = useState('pages');
  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Permissions</h1>
            <p className="muted">Who can sign in to Nano Utilities, and which pages each person can view or edit.</p>
          </div>
        </div>
        <div className="view-toggle ticket-tabs" role="tablist" aria-label="Permissions">
          <button type="button" role="tab" aria-selected={tab === 'pages'} className={`btn${tab === 'pages' ? ' active' : ''}`} onClick={() => setTab('pages')}>Page access</button>
          <button type="button" role="tab" aria-selected={tab === 'signin'} className={`btn${tab === 'signin' ? ' active' : ''}`} onClick={() => setTab('signin')}>Sign-in access</button>
          <button type="button" role="tab" aria-selected={tab === 'profiles'} className={`btn${tab === 'profiles' ? ' active' : ''}`} onClick={() => setTab('profiles')}>Profiles</button>
        </div>
      </section>
      {tab === 'pages' && <section className="card"><PageAccess me={me} /></section>}
      {tab === 'signin' && <SignInAccess me={me} />}
      {tab === 'profiles' && <Profiles />}
    </div>
  );
}

function SignInAccess({ me }) {
  const [users, setUsers] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState(null);
  const [saving, setSaving] = useState({});
  const [notice, setNotice] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [applying, setApplying] = useState(null);
  const [canManageAdmins, setCanManageAdmins] = useState(false);
  const profiles = useProfiles();
  // Only the owners (Ruan, Wilhelm, Dirk) make or change master admins.
  const levelsFor = (u) => (canManageAdmins || u?.level === 'admin' ? LEVELS : LEVELS.filter((l) => l.value !== 'admin'));
  const locked = (u) => u.level === 'admin' && !canManageAdmins;

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api('/access/users');
      setUsers(r.users);
      setCanManageAdmins(r.canManageAdmins !== false);
    } catch (e) {
      setLoadError(problem(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!users) return [];
    return users.filter((u) => (!groupFilter || u.level === groupFilter)
      && (!f || u.name.toLowerCase().includes(f) || u.email.includes(f)));
  }, [users, filter, groupFilter]);

  const counts = useMemo(() => {
    const c = Object.fromEntries(LEVELS.map((l) => [l.value, 0]));
    for (const u of users || []) c[u.level] += 1;
    return c;
  }, [users]);

  // With a profile, their pages are set from it too (Add user, Apply profile).
  const setLevel = async (user, level, profile = null) => {
    setSaving((s) => ({ ...s, [user.id]: true }));
    setNotice(null);
    try {
      if (level !== user.level) await api(`/access/users/${user.id}`, { method: 'PUT', body: { level } });
      let pages = '';
      if (profile && level !== 'none' && level !== 'field') {
        await api(`/permissions/people/${user.id}/profile`, { method: 'POST', body: { profileId: profile.id, person: { name: user.name, email: user.email, signIn: level } } });
        pages = ` Pages set from the ${profile.name} profile.`;
      }
      const did = level === 'none' ? `${user.name} no longer has access.` : level === user.level ? `${user.name}:` : `${user.name} is now ${LABEL[level]}.`;
      setNotice({ tone: 'ok', text: `${did}${pages}` });
      await load();
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    } finally {
      setSaving((s) => ({ ...s, [user.id]: false }));
    }
  };

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h2>Sign-in access</h2>
            <p className="muted">Who can use Nano Utilities, and whether they get the field app, the office web, or both. Each is one NU- group in Microsoft 365.</p>
          </div>
          <button type="button" className="btn primary with-icon" onClick={() => setAdding(true)}>
            <UserPlus size={18} aria-hidden="true" /> Add user
          </button>
        </div>
        <p className="muted small">Changes apply the next time the person signs in or their session refreshes (within about an hour).</p>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
      </section>

      <div className="group-cards" role="group" aria-label="Access groups">
        {LEVELS.map((l) => (
          <button
            key={l.value}
            type="button"
            className={`group-card${groupFilter === l.value ? ' active' : ''}`}
            aria-pressed={groupFilter === l.value}
            onClick={() => setGroupFilter((g) => (g === l.value ? null : l.value))}
          >
            <span className="group-count">{users ? counts[l.value] : '–'}</span>
            <strong>{l.label}</strong>
            <span className="muted small">{l.hint}</span>
            <span className="group-name small">{l.group}</span>
          </button>
        ))}
      </div>

      <section className="card">
        {groupFilter && (
          <p className="small filter-line">
            Showing <strong>{LABEL[groupFilter]}</strong> only.{' '}
            <button type="button" className="link-btn" onClick={() => setGroupFilter(null)}>Show everyone</button>
          </p>
        )}
        <label className="search">
          <Search size={18} aria-hidden="true" />
          <input type="search" placeholder="Filter by name or email" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </label>

        {loadError && (
          <div className="empty">
            <p>{loadError}</p>
            <button type="button" className="btn" onClick={load}>Try again</button>
          </div>
        )}
        {!loadError && !users && <p className="muted">Loading…</p>}
        {users && shown.length === 0 && <p className="muted">{!users.length ? 'Nobody has access yet. Use Add user.' : groupFilter && !filter.trim() ? `Nobody is in ${LABEL[groupFilter]} yet.` : 'Nobody matches that filter.'}</p>}

        {shown.length > 0 && (
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Access</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {shown.map((u) => {
                const self = u.id === me.id;
                const fixed = self || locked(u);
                return (
                  <tr key={u.id}>
                    <td data-label="Name">
                      <strong>{u.name}</strong>{self && <span className="tag">You</span>}
                      {u.jobTitle && <div className="muted small">{u.jobTitle}</div>}
                      {u.extraGroups && <div className="warn-text small">In more than one group: choosing a level tidies this up.</div>}
                    </td>
                    <td data-label="Email" className="muted">{u.email}</td>
                    <td data-label="Access">
                      <select
                        value={u.level}
                        disabled={saving[u.id] || fixed}
                        title={self ? 'You cannot change your own access' : fixed ? 'Only Ruan, Wilhelm or Dirk can change a master admin' : undefined}
                        onChange={(e) => setLevel(u, e.target.value)}
                        aria-label={`Access for ${u.name}`}
                      >
                        {levelsFor(u).map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                    </td>
                    <td className="row-actions">
                      {u.level !== 'field' && profiles.length > 0 && (
                        <button type="button" className="link-btn" disabled={saving[u.id]} onClick={() => setApplying(u)}>
                          Apply profile
                        </button>
                      )}
                      {!fixed && (
                        <button type="button" className="link-btn danger" disabled={saving[u.id]} onClick={() => setConfirmRemove(u)}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <AddUserModal
        open={adding}
        existing={users || []}
        profiles={profiles}
        levels={canManageAdmins ? LEVELS : LEVELS.filter((l) => l.value !== 'admin')}
        onClose={() => setAdding(false)}
        onAdd={async (person, level, profile) => {
          setAdding(false);
          const now = (users || []).find((u) => u.id === person.id);
          await setLevel({ ...person, level: now?.level || 'none' }, level, profile);
        }}
      />

      <ApplyProfileModal
        user={applying}
        profiles={profiles}
        onClose={() => setApplying(null)}
        onApply={async (profile) => { const u = applying; setApplying(null); await setLevel(u, u.level, profile); }}
      />

      <Modal
        open={Boolean(confirmRemove)}
        title="Remove access?"
        onClose={() => setConfirmRemove(null)}
        footer={(
          <>
            <button type="button" className="btn" onClick={() => setConfirmRemove(null)}>Cancel</button>
            <button type="button" className="btn danger-btn" onClick={() => { const u = confirmRemove; setConfirmRemove(null); setLevel(u, 'none'); }}>
              Remove access
            </button>
          </>
        )}
      >
        <p><strong>{confirmRemove?.name}</strong> will no longer be able to use the Nano Utilities field app or office web. Their Microsoft 365 account is not affected.</p>
      </Modal>
    </div>
  );
}

function AddUserModal({ open, existing, profiles, levels = LEVELS, onClose, onAdd }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);
  const [level, setLevel] = useState('field');
  const [profileId, setProfileId] = useState('');

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); setPicked(null); setLevel('field'); setProfileId(''); setError(null); }
  }, [open]);

  // Search as you type, but only after a short pause.
  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        setResults((await api(`/access/people?q=${encodeURIComponent(q.trim())}`, { signal: ctrl.signal })).people);
      } catch (e) {
        if (e.name !== 'AbortError') setError(problem(e));
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, open]);

  const already = new Map(existing.map((u) => [u.id, u.level]));

  return (
    <Modal
      open={open}
      title="Add user"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={!picked} onClick={() => onAdd(picked, level, level === 'field' ? null : profiles.find((p) => String(p.id) === profileId) || null)}>
            {picked && already.has(picked.id) ? 'Change access' : 'Add'}
          </button>
        </>
      )}
    >
      <div className="stack">
        <label className="search">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            autoFocus
            placeholder="Search Microsoft 365 by name or email"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPicked(null); }}
          />
        </label>
        {searching && <p className="muted small">Searching…</p>}
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!searching && q.trim().length >= 2 && results.length === 0 && !error && <p className="muted small">No one found.</p>}
        {results.length > 0 && (
          <ul className="pick-list">
            {results.map((p) => (
              <li key={p.id}>
                <button type="button" className={`pick${picked?.id === p.id ? ' picked' : ''}`} onClick={() => setPicked(p)}>
                  <strong>{p.name}</strong>
                  <span className="muted small">{p.email}{p.jobTitle ? ` · ${p.jobTitle}` : ''}</span>
                  {already.has(p.id) && <span className="tag">Has access: {LABEL[already.get(p.id)]}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {picked && (
          <fieldset className="levels">
            <legend>Access for {picked.name}</legend>
            {levels.map((l) => (
              <label key={l.value} className="level-opt">
                <input type="radio" name="level" value={l.value} checked={level === l.value} onChange={() => setLevel(l.value)} />
                <span><strong>{l.label}</strong> <span className="muted small">{l.hint}</span></span>
              </label>
            ))}
          </fieldset>
        )}
        {picked && level !== 'field' && (
          <label className="fld">
            <span>Pages</span>
            <select value={profileId} onChange={(e) => setProfileId(e.target.value)}>
              <option value="">{level === 'admin' ? 'All pages (master admin)' : 'No pages yet (set them on Page access)'}</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <span className="muted small">
              {profileId ? profiles.find((p) => String(p.id) === profileId)?.description : 'New people start with no pages.'}
              {already.has(picked.id) && profileId ? ' Replaces the pages they have now.' : ''}
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}

function useProfiles() {
  const [list, setList] = useState([]);
  useEffect(() => { api('/permissions/profiles').then((d) => setList(d.profiles), () => {}); }, []);
  return list;
}

// A profile's pages as short text: "Bookings list (edit), Incidents (view)".
const pageLines = (pages) => PAGES.filter((p) => pages[p.id])
  .map((p) => `${p.label}${p.levels.length > 1 ? ` (${pages[p.id] === 'edit' ? 'edit' : 'view'})` : ''}`);

function ApplyProfileModal({ user, profiles, onClose, onApply }) {
  const [id, setId] = useState('');
  useEffect(() => { setId(''); }, [user]);
  const profile = profiles.find((p) => String(p.id) === id);
  return (
    <Modal
      open={Boolean(user)}
      title={user ? `Apply a profile to ${user.name}` : ''}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={!profile} onClick={() => onApply(profile)}>Apply</button>
        </>
      )}
    >
      <div className="stack">
        <label className="fld">
          <span>Profile</span>
          <select value={id} onChange={(e) => setId(e.target.value)} autoFocus>
            <option value="">Choose…</option>
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        {profile && (
          <>
            {profile.description && <p className="small">{profile.description}</p>}
            <p className="small"><strong>Pages:</strong> {pageLines(profile.pages).join(', ') || 'none'}</p>
            <p className="small warn-text">This replaces {user?.name}’s page access (not the helpdesk queue). You can still change single pages afterwards.</p>
          </>
        )}
      </div>
    </Modal>
  );
}

// Only the levels the page has: one level reads as "Has access".
const levelOptions = (page) => (page.levels.length === 1
  ? [['', 'No access'], [page.levels[0], 'Has access']]
  : [['', 'No access'], ['view', 'Can view'], ['edit', 'Can edit']]);

// Profiles tab: named sets of pages. A starting point only: changing one
// later changes nobody.
function Profiles() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState(null);
  const load = useCallback(() => api('/permissions/profiles').then(
    (d) => setList(d.profiles),
    (e) => setNotice({ tone: 'bad', text: problem(e) }),
  ), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { name: editing.name, description: editing.description, pages: editing.pages };
    try {
      if (editing.id) await api(`/permissions/profiles/${editing.id}`, { method: 'PUT', body });
      else await api('/permissions/profiles', { method: 'POST', body });
      setNotice({ tone: 'ok', text: `${editing.name} saved.` });
      setEditing(null);
      load();
    } catch (e) {
      setEditing((x) => ({ ...x, error: problem(e) }));
    }
  };
  const remove = async (p) => {
    try {
      await api(`/permissions/profiles/${p.id}`, { method: 'DELETE' });
      setNotice({ tone: 'ok', text: `${p.name} deleted. Nobody’s access changed.` });
      setEditing(null);
      load();
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    }
  };
  const setPage = (id, value) => setEditing((x) => {
    const pages = { ...x.pages };
    if (value) pages[id] = value; else delete pages[id];
    return { ...x, pages };
  });
  const pages = PAGES.filter((p) => p.id !== 'helpdesk-queue');

  return (
    <section className="card stack">
      <div className="page-head">
        <div>
          <h2>Profiles</h2>
          <p className="muted">A set of pages to give someone in one go: pick one when you add a person on Sign-in access, or use Apply profile. It’s a starting point: changing a profile later doesn’t change anyone, and every page it sets shows in the Audit trail. The helpdesk queue isn’t part of profiles.</p>
        </div>
        <button type="button" className="btn primary with-icon" onClick={() => setEditing({ name: '', description: '', pages: {} })}>
          <Plus size={18} aria-hidden="true" /> New profile
        </button>
      </div>
      {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
      {!list && !notice && <p className="muted">Loading…</p>}
      {list && !list.length && <p className="muted">No profiles yet.</p>}
      {list?.map((p) => (
        <button key={p.id} type="button" className="profile-card" onClick={() => setEditing({ ...p })}>
          <strong>{p.name}</strong>
          {p.description && <span className="small">{p.description}</span>}
          <span className="small muted">{pageLines(p.pages).join(', ') || 'No pages'}</span>
        </button>
      ))}

      <Modal
        open={Boolean(editing)}
        wide
        title={editing?.id ? `Edit ${editing.name}` : 'New profile'}
        onClose={() => setEditing(null)}
        footer={editing && (
          <>
            {editing.id && (
              <button type="button" className="btn danger-btn with-icon" onClick={() => remove(editing)}>
                <Trash2 size={16} aria-hidden="true" /> Delete
              </button>
            )}
            <button type="button" className="btn" onClick={() => setEditing(null)}>Cancel</button>
            <button type="button" className="btn primary" disabled={!editing.name.trim()} onClick={save}>Save</button>
          </>
        )}
      >
        {editing && (
          <div className="stack">
            <div className="fld-row">
              <label className="fld">
                <span>Name</span>
                <input value={editing.name} maxLength={60} autoFocus onChange={(e) => setEditing((x) => ({ ...x, name: e.target.value }))} />
              </label>
              <label className="fld">
                <span>Description</span>
                <input value={editing.description} maxLength={255} onChange={(e) => setEditing((x) => ({ ...x, description: e.target.value }))} />
              </label>
            </div>
            <table className="table">
              <thead><tr><th>Page</th><th>Access</th></tr></thead>
              <tbody>
                {PAGE_GROUPS.flatMap((g) => {
                  const inGroup = pages.filter((p) => p.group === g.id);
                  if (!inGroup.length) return [];
                  return [
                    <tr key={g.id}><td colSpan={2}><strong className="small muted">{g.label}</strong></td></tr>,
                    ...inGroup.map((p) => (
                      <tr key={p.id}>
                        <td>{p.label}</td>
                        <td>
                          <select value={editing.pages[p.id] || ''} aria-label={`${p.label} access`} onChange={(e) => setPage(p.id, e.target.value)}>
                            {levelOptions(p).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                      </tr>
                    )),
                  ];
                })}
              </tbody>
            </table>
            {editing.error && <p className="notice-line notice-bad">{editing.error}</p>}
          </div>
        )}
      </Modal>
    </section>
  );
}
